/**
 * For-block theme — CSS styles for ::for / ::endfor decorations.
 */

import { EditorView } from "@codemirror/view";
import {
    CM_MONO_FONT_FAMILY,
    accentIconStyles,
    accentText,
    directiveLineStyles,
    directivePrettyStyles,
    statusLineStyles,
} from "../shared/theme-helpers";

const FOR_ACCENT = "--cm-for-accent";
const RUNNING_ACCENT = "--status-running";
const SUCCESS_ACCENT = "--status-success";
const ERROR_ACCENT = "--status-error";

export const forTheme = EditorView.baseTheme({
    /* ── For line background (shared across all for lines) ── */
    ".cm-magic-for-line": directiveLineStyles(FOR_ACCENT, 4, 20),
    ".cm-magic-for-line-running": statusLineStyles(RUNNING_ACCENT, {
        backgroundPercent: 8,
        borderPercent: 100,
    }),
    ".cm-magic-for-line-success": statusLineStyles(SUCCESS_ACCENT, {
        backgroundPercent: 4,
        borderPercent: 30,
    }),
    ".cm-magic-for-line-error": statusLineStyles(ERROR_ACCENT, {
        backgroundPercent: 4,
        borderPercent: 30,
    }),
    ".cm-magic-for-active .cm-magic-btn-group": {
        opacity: "1",
        maxWidth: "120px",
        pointerEvents: "auto",
    },
    /* ── Pretty for header ── */
    ".cm-magic-for-pretty": directivePrettyStyles(FOR_ACCENT),
    ".cm-magic-for-pretty-icon": accentIconStyles(FOR_ACCENT),
    ".cm-magic-for-pretty-keyword": accentText(FOR_ACCENT, {
        fontWeight: "600",
        letterSpacing: "0.01em",
    }),
    ".cm-magic-for-pretty-id": {
        color: "var(--color-foreground)",
        fontWeight: "500",
    },
    ".cm-magic-for-pretty-var": {
        color: "var(--cm-ref-pending)",
    },
    ".cm-magic-for-pretty-in": {
        color: "var(--color-muted-foreground)",
        fontSize: "11px",
        margin: "0 1px",
    },
    ".cm-magic-for-pretty-source": {
        display: "inline-flex",
        alignItems: "center",
        gap: "2px",
        color: "var(--color-muted-foreground)",
        fontSize: "11px",
        maxWidth: "240px",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    ".cm-magic-for-pretty-template": {
        color: "var(--color-muted-foreground)",
        fontFamily: CM_MONO_FONT_FAMILY,
        fontSize: "11px",
        opacity: "0.68",
    },
    ".cm-magic-for-pretty-source-ref": {
        color: "var(--cm-ref-pending)",
        cursor: "pointer",
    },
    ".cm-magic-for-pretty-ref-icon": {
        width: "14px",
        height: "14px",
        minWidth: "14px",
        fontSize: "10px",
        backgroundColor: "var(--cm-ref-pending)",
        color: "var(--color-background)",
    },
    ".cm-magic-for-pretty-ref-id": {
        fontWeight: "500",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    ".cm-magic-for-pretty-ref-path": {
        color: "var(--color-muted-foreground)",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    /* ── Run button ── */
    ".cm-magic-for-run-btn": {
        display: "inline-flex",
        alignItems: "center",
        fontSize: "11px",
        padding: "1px 6px",
        marginLeft: "6px",
        borderRadius: "3px",
        border: "1px solid var(--color-border)",
        backgroundColor: "var(--color-background)",
        color: "var(--color-foreground)",
        cursor: "pointer",
        userSelect: "none",
        "&:hover": {
            backgroundColor: "var(--color-muted)",
        },
    },
    ".cm-magic-for-run-btn-running": {
        color: "var(--status-running)",
        borderColor: "var(--status-running)",
        cursor: "wait",
    },
    ".cm-magic-for-run-btn-success": {
        color: "var(--status-success)",
        borderColor: "var(--status-success)",
    },
    ".cm-magic-for-run-btn-error": {
        color: "var(--status-error)",
        borderColor: "var(--status-error)",
    },
    ".cm-magic-for-run-spinner": {
        display: "inline-block",
        width: "8px",
        height: "8px",
        borderRadius: "50%",
        border: "1.5px solid var(--status-running)",
        borderTopColor: "transparent",
        animation: "cm-magic-spin 0.6s linear infinite",
        verticalAlign: "middle",
    },

    /* ── Endfor pretty ── */
    ".cm-magic-for-end-pretty": {
        display: "inline-flex",
        alignItems: "center",
        fontSize: "13px",
        fontFamily: "ui-sans-serif, system-ui, sans-serif",
        color: "var(--color-muted-foreground)",
        opacity: "0.5",
    },
});
